import { useTheme } from '@approvalmax/theme';
import { CheckboxEditor, GlobalHotKeys } from '@approvalmax/ui';
import { hooks } from '@approvalmax/utils';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { FC, memo, useMemo } from 'react';
import { useMedia } from 'react-use';

import { messages } from './AcceptInvitationsStep.messages';
import {
    Description,
    InvitationItem,
    InvitationLeft,
    InvitationNote,
    InvitationRight,
    InvitationTitle,
    List,
    Root,
    Title,
} from './AcceptInvitationsStep.styles';
import { AcceptInvitationsStepProps } from './AcceptInvitationsStep.types';

const AcceptInvitationsStep: FC<AcceptInvitationsStepProps> = memo((props) => {
    const { acceptedInvitationIds, onAcceptedChange, invitations, onNext } = props;

    const onNextRef = hooks.useWrapInRef(onNext);
    const handlers = useMemo(
        () => ({
            enter: () => onNextRef.current(),
        }),
        [onNextRef]
    );

    const respond = (invitationId: string, decision: domain.CompanyInvitationResponse) => {
        let newValue = acceptedInvitationIds;

        if (decision === domain.CompanyInvitationResponse.Accepted) {
            if (!acceptedInvitationIds.includes(invitationId)) {
                newValue = newValue.concat(invitationId);
            }
        } else {
            newValue = newValue.filter((x) => x !== invitationId);
        }

        if (newValue !== acceptedInvitationIds) {
            onAcceptedChange(newValue);
        }
    };

    const { theme } = useTheme();
    const isMobileScreen = useMedia(`(max-width: ${theme.container.breakpoint.xsmall})`);

    return (
        <GlobalHotKeys handlers={handlers} onlyTriggerWhenNoFocus>
            <Root>
                {!isMobileScreen && (
                    <>
                        <Title>{messages.title}</Title>

                        <Description>{messages.description}</Description>
                    </>
                )}

                <List>
                    {invitations.map((invitation) => {
                        const accept = acceptedInvitationIds.includes(invitation.companyId);

                        return (
                            <InvitationItem key={invitation.companyId}>
                                <InvitationLeft>
                                    <InvitationTitle>{invitation.companyName}</InvitationTitle>

                                    <InvitationNote>
                                        {messages.invitedBy({
                                            name: selectors.user.getDisplayName(
                                                invitation.inviterUserFirstName,
                                                invitation.inviterUserLastName,
                                                invitation.inviterUserEmail
                                            ),
                                        })}
                                    </InvitationNote>
                                </InvitationLeft>

                                <InvitationRight>
                                    <CheckboxEditor
                                        onChange={(newValue) =>
                                            respond(
                                                invitation.companyId,
                                                newValue
                                                    ? domain.CompanyInvitationResponse.Accepted
                                                    : domain.CompanyInvitationResponse.Declined
                                            )
                                        }
                                        value={accept}
                                    />
                                </InvitationRight>
                            </InvitationItem>
                        );
                    })}
                </List>
            </Root>
        </GlobalHotKeys>
    );
});

export default AcceptInvitationsStep;
